import { Card, Language, ListStyle } from '@yleisradio/areena-types';
import { GroupEngagementData } from 'components/SnowplowEngagementAnalytics/types';
import logger from 'services/logger';
import { Consent } from 'types/yleConsentSdk';
import { randomUUID } from 'utils/uuid';

export type GenericContext = {
  schema: string;
  data: Record<string, unknown>;
};

export type EntityType =
  | 'areena_package'
  | 'areena_view'
  | 'program'
  | 'series'
  | 'areena_custom_view';

export type ViewModeType =
  | 'full'
  | 'lift'
  | 'overlay'
  | 'summary'
  | 'teaser'
  | 'thumbnail';

function uiLanguageToSnowplowLanguage(language: Language): 'fin' | 'swe' {
  switch (language) {
    case 'fi':
      return 'fin';
    case 'sv':
      return 'swe';
    default:
      throw new Error(`Unsupported language: ${language}`);
  }
}

// https://github.com/Yleisradio/datacloud-snowplow/blob/main/snowplow-structures/data-structures/fi.yle/yle_context.yaml
export const yleContextGenerator = ({
  language,
  section,
  countryCode,
}: {
  language: Language;
  section: string;
  countryCode: string | undefined;
}) =>
  ({
    schema: 'iglu:fi.yle/yle_context/jsonschema/2-12-0',
    data: {
      main_service: 'yle-areena',
      service: 'areena-web',
      section,
      country_code: countryCode || null,
      service_language: uiLanguageToSnowplowLanguage(language),
      content_language: uiLanguageToSnowplowLanguage(language),
      wrapper_type: 'web', // ["web”, "mob”, "webview”, "iframe”, "chromecast_receiver", …]
      context_index: 1, // wrapper context index. > 1 means measuring happened inside iframe / webview etc.
    },
  }) satisfies GenericContext;

export const userProfileGenerator = (age: number | undefined) => ({
  schema: 'iglu:fi.yle.content/user_profile/jsonschema/2-2-0',
  data: {
    age_group: age ?? null,
    predicate: 'consumer',
  },
});

export const yleConsentGenerator = (consent: Consent) =>
  ({
    schema: 'iglu:fi.yle/consent/jsonschema/2-0-1',
    data: {
      version: `v${consent.version}`,
      development: consent.development,
      personalization: consent.personalization,
      embedded_social_media: consent.embedded_social_media,
      targeting: consent.targeting,
    },
  }) satisfies GenericContext;

// https://github.com/Yleisradio/datacloud-snowplow/blob/main/snowplow-structures/data-structures/fi.yle.engagement/view.yaml
export const viewEventGenerator = (
  viewUuid: string,
  viewType: 'entity' | 'ui_element',
  viewMode: ViewModeType
) =>
  ({
    schema: 'iglu:fi.yle.engagement/view/jsonschema/2-1-0',
    data: {
      view_id: viewUuid,
      view_type: viewType,
      ...(viewType === 'entity' && { view_entity_type: 'entity' }),
      view_mode: viewMode,
    },
  }) satisfies GenericContext;

export const clickEventGenerator = (
  action: 'link' | 'play',
  viewUuid: string
) =>
  ({
    schema: 'iglu:fi.yle.engagement/click/jsonschema/2-2-0',
    data: {
      click_id: randomUUID(),
      view_id: viewUuid,
      action,
    },
  }) satisfies GenericContext;

export const entityContextGenerator = (entityId: string) =>
  ({
    schema: 'iglu:fi.yle.content/entity/jsonschema/2-0-1',
    data: {
      entity_id: entityId,
      predicate: 'engagement_view_target',
    },
  }) satisfies GenericContext;

export const wrapperContextGenerator = (label?: string | undefined) =>
  ({
    schema: 'iglu:fi.yle.ui/wrapper/jsonschema/2-3-0',
    data: {
      type: 'main',
      ...(label ? { label } : {}),
    },
  }) satisfies GenericContext;

const uiGroupContextSchema = 'iglu:fi.yle.ui/group/jsonschema/2-3-0';

export const groupContextGeneratorForHighlightCard = (
  label: string | undefined
) =>
  ({
    schema: uiGroupContextSchema,
    data: {
      is_paginated: false,
      num_items: 1,
      type: 'highlight_card',
      ...(label ? { label } : {}),
    },
  }) satisfies GenericContext;

export const groupContextGeneratorForList = (
  groupData: GroupEngagementData | undefined
) => {
  if (!groupData) return undefined;

  const { isPaginated, label, length, referer, style, url } = groupData;
  return {
    schema: uiGroupContextSchema,
    data: {
      is_paginated: isPaginated,
      num_items: length,
      ...(label ? { label } : {}),
      ...(referer ? { source_ref: referer } : {}),
      ...(url ? { source_url: url } : {}),
      type: style?.layout === 'grid' ? 'grid' : 'list',
    },
  } satisfies GenericContext;
};

export const groupContextGeneratorForGuide = (label: string | undefined) => {
  return {
    schema: uiGroupContextSchema,
    data: {
      is_paginated: false,
      num_items: null,
      type: 'grid',
      label,
    },
  } satisfies GenericContext;
};

export const groupContextGeneratorForVideoFeed = (label: string | undefined) =>
  ({
    schema: uiGroupContextSchema,
    data: {
      is_paginated: false,
      num_items: null,
      type: 'list',
      ...(label ? { label } : {}),
      source_ref: 'video-feed-widget',
    },
  }) satisfies GenericContext;

export const articleContextGenerator = (articleId: string) =>
  ({
    schema: 'iglu:fi.yle.content/article/jsonschema/2-0-0',
    data: {
      article_id: articleId,
      predicate: 'engagement_view_target',
    },
  }) satisfies GenericContext;

type ElementContextGeneratorParams = {
  elementIndex: number | undefined;
  groupIndex: number | undefined;
  listStyle: ListStyle | undefined;
  title: string | undefined;
  type: 'card' | 'highlight_card' | 'guide';
};

export const elementContextGenerator = ({
  elementIndex,
  groupIndex,
  listStyle,
  title,
  type,
}: ElementContextGeneratorParams) =>
  ({
    schema: 'iglu:fi.yle.ui/element/jsonschema/2-1-0',
    data: {
      group_index: elementIndex ?? null,
      group_order_index: groupIndex ?? null,
      type,
      wrapper_index: null,
      ...(listStyle?.size && { layout_size: listStyle.size }),
      ...(title && { title }),
    },
  }) satisfies GenericContext;

export const imageContextGenerator = (imageId: string) =>
  ({
    schema: 'iglu:fi.yle.content/image/jsonschema/2-1-0',
    data: {
      image_id: imageId,
      predicate: 'engagement_view_source',
    },
  }) satisfies GenericContext;

export const activeEntityGenerator = (
  id: string | undefined,
  version: string | undefined
): GenericContext | null => {
  if (id) {
    const versionNumber = parseInt(version || '');
    return {
      schema: 'iglu:fi.yle.content/areena_view/jsonschema/2-1-0',
      data: {
        areena_view_id: id,
        version: isNaN(versionNumber) ? null : versionNumber,
        predicate: 'consumed_activity',
      },
    };
  }
  return null;
};

export const recommendationContextGenerator = (
  recommendation_id: string,
  method: string
) => ({
  schema: 'iglu:fi.yle.content/recommendation/jsonschema/2-5-0',
  data: {
    recommendation_id,
    method,
    predicate: 'engagement_view_source',
  },
});

export const areenaPackageContextGenerator = (
  id: string,
  predicate: 'consumed_activity' | 'engagement_view_source'
) => ({
  schema: 'iglu:fi.yle.content/areena_package/jsonschema/2-0-0',
  data: {
    areena_package_id: id,
    predicate,
  },
});

const customViewContextGenerator = (areena_custom_view_identifier: string) => ({
  schema: 'iglu:fi.yle.content/areena_custom_view/jsonschema/2-0-0',
  data: {
    predicate: 'consumed_activity',
    areena_custom_view_identifier,
  },
});

const seriesContextGenerator = (seriesId: string, predicate: string) => ({
  schema: 'iglu:fi.yle.content/series/jsonschema/2-0-0',
  data: {
    series_id: seriesId,
    predicate: predicate,
  },
});

export const programContextGenerator = (
  programId: string,
  predicate: string
) => ({
  schema: 'iglu:fi.yle.content/program/jsonschema/2-0-1',
  data: {
    program_id: programId,
    predicate: predicate,
  },
});

export const activeEntityContextGenerator = (
  type: string | undefined,
  id: string | undefined,
  version: string | undefined,
  areenaViewType: string | undefined
) => {
  if (id === undefined) {
    logger.error('id is required for activeEntityContextGenerator');
    return null;
  }
  const predicate = 'consumed_activity';

  switch (type) {
    case 'areena_custom_view':
      return customViewContextGenerator(id);
    case 'areena_view':
      return activeEntityGenerator(id, version);
    case 'series':
      return seriesContextGenerator(id, predicate);
    case 'areena_package':
      return areenaPackageContextGenerator(id, predicate);
    case 'program':
      return programContextGenerator(id, predicate);
    case 'areena_guide':
      return areenaGuideContextGenerator(id, areenaViewType, predicate);
    default:
      logger.error(`Unknown type: ${type}`);
      return null;
  }
};

export const abTestContextGenerator = (name: string, group: string) => ({
  schema: 'iglu:fi.yle.ab/test/jsonschema/2-1-0',
  data: {
    name,
    group,
  },
});

/**
 *
 * @param date string YYYY-MM-DD
 * @param type string Guide type (tv or radio).
 * @returns
 */

export const areenaGuideContextGenerator = (
  date: string | undefined,
  areenaViewType: string | undefined,
  areenaGuideContextPredicate:
    | 'engagement_view_target'
    | 'engagement_view_source'
    | 'consumed_activity'
) => {
  if (!date || !areenaViewType || !areenaGuideContextPredicate) return null;

  return {
    schema: 'iglu:fi.yle.content/areena_guide/jsonschema/2-1-0',
    data: {
      date,
      type: areenaViewType,
      predicate: areenaGuideContextPredicate,
    },
  };
};

export type SummaryViewEventParams = {
  viewUuid: string;
  card: Card;
};

export type GuideViewContextGeneratorParams = {
  date: string | undefined;
  label: string | undefined;
  programId?: string | undefined;
  entityId?: string | undefined;
  areenaViewType: string | undefined;
  areenaGuideContextPredicate:
    | 'engagement_view_target'
    | 'engagement_view_source';
};

export type GuideViewEventGeneratorParams = {
  viewUuid: string;
  date: string | undefined;
  title: string | undefined;
  programId?: string | undefined;
  entityId?: string | undefined;
  areenaViewType: string | undefined;
  viewMode: 'full' | 'summary';
  areenaGuideContextPredicate:
    | 'engagement_view_target'
    | 'engagement_view_source';
};

export const guideViewContextGenerator = ({
  date,
  label,
  programId,
  entityId,
  areenaViewType,
  areenaGuideContextPredicate,
}: GuideViewContextGeneratorParams) => {
  const elementIndex = 0;
  const groupIndex = 0;
  const type = 'guide';

  const context = [
    wrapperContextGenerator(),
    areenaGuideContextGenerator(
      date,
      areenaViewType,
      areenaGuideContextPredicate
    ),
    groupContextGeneratorForGuide(label),
    elementContextGenerator({
      elementIndex,
      groupIndex,
      listStyle: undefined,
      title: undefined,
      type,
    }),
    ...(entityId ? [entityContextGenerator(entityId)] : []),
    ...(programId
      ? [programContextGenerator(programId, 'engagement_view_target')]
      : []),
  ];
  return context;
};
